
import { supabase } from '@/integrations/supabase/client';

export interface PublicProfile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  location: string | null;
  created_at: string;
}

// 🔒 SECURE FILTER UTILITY - Consistent filtering across all functions
const filterSafeUserFields = (userData: any): PublicProfile | null => {
  if (!userData) return null;
  
  return {
    id: userData.id,
    full_name: userData.full_name,
    avatar_url: userData.avatar_url,
    bio: userData.bio,
    location: userData.location,
    created_at: userData.created_at
    // ❌ Excluded: email, subscription_tier, payment_provider, etc.
  };
};

// Get public profile data securely (no email exposure)
export const getPublicProfile = async (userId: string): Promise<PublicProfile | null> => {
  try {
    const { data, error } = await supabase.rpc('get_public_profile', {
      profile_user_id: userId
    });

    if (error) throw error;
    return data?.[0] || null;
  } catch (error) {
    console.error('Error fetching public profile:', error);
    return null;
  }
};

// 🔒 SECURE: Search public users using new RPC function
export const searchPublicUsers = async (searchTerm: string): Promise<PublicProfile[]> => {
  try {
    if (!searchTerm || searchTerm.trim().length < 2) {
      return [];
    }

    console.log('🔍 Secure public user search:', { searchTerm, method: 'RPC_secure_function' });

    // 🔒 NEW: Using secure RPC function instead of direct table access
    const { data, error } = await supabase.rpc('search_public_users_secure', {
      search_term: searchTerm.trim()
    });

    if (error) {
      console.error('Error searching public users:', error);
      return [];
    }

    // 🔒 Apply consistent security filtering (extra safety layer)
    return (data || []).map(user => filterSafeUserFields(user)).filter(Boolean) as PublicProfile[];
  } catch (error) {
    console.error('Error in searchPublicUsers:', error);
    return [];
  }
};

// 🔒 SECURE: Get multiple public profiles using new RPC function
export const getMultiplePublicProfiles = async (userIds: string[]): Promise<PublicProfile[]> => {
  try {
    if (!userIds || userIds.length === 0) {
      return [];
    }

    console.log('🔍 Secure bulk profile fetch:', { userCount: userIds.length, method: 'RPC_secure_function' });

    // 🔒 NEW: Using secure RPC function instead of direct table access
    const { data, error } = await supabase.rpc('get_multiple_public_profiles_secure', {
      user_ids: userIds
    });

    if (error) {
      console.error('Error fetching multiple public profiles:', error);
      return [];
    }

    // 🔒 Apply consistent security filtering (extra safety layer)
    return (data || []).map(user => filterSafeUserFields(user)).filter(Boolean) as PublicProfile[];
  } catch (error) {
    console.error('Error in getMultiplePublicProfiles:', error);
    return [];
  }
};

// Get current user's own profile (includes sensitive data)
export const getOwnProfile = async () => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return null;

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.user.id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching own profile:', error);
    return null;
  }
};

// Update profile with security validation (✅ FASE 2: Migrado a profiles)
export const updateProfile = async (updates: Partial<{
  full_name: string;
  bio: string;
  location: string;
  avatar_url: string;
}>) => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('Not authenticated');

    // Map full_name to display_name for profiles table
    const safeUpdates = {
      display_name: updates.full_name,  // ✅ Mapeo: full_name → display_name
      bio: updates.bio,
      location: updates.location,
      avatar_url: updates.avatar_url,
      updated_at: new Date().toISOString(),
    };

    // Remove undefined values
    Object.keys(safeUpdates).forEach(key => {
      if (safeUpdates[key as keyof typeof safeUpdates] === undefined) {
        delete safeUpdates[key as keyof typeof safeUpdates];
      }
    });

    // ✅ FASE 2: Actualizar profiles en lugar de users
    const { data, error } = await supabase
      .from('profiles')
      .update(safeUpdates)
      .eq('user_id', user.user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};
